import {
  DescriptionOutlined as DescriptionOutlinedIcon,
  Download as DownloadIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  SlideshowOutlined as SlideshowOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import { useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { DocumentFormat, DocumentSummary } from "@/api/types";
import { documentDownloadHref } from "@/features/documents/documentLinks";

const LABEL_KEYS: Record<DocumentFormat, string> = {
  docx: "documents_ui.download_docx",
  pdf: "documents_ui.download_pdf",
  pptx: "documents_ui.download_pptx",
};

const ICONS: Record<DocumentFormat, React.ReactNode> = {
  docx: <DescriptionOutlinedIcon fontSize="small" />,
  pdf: <PictureAsPdfOutlinedIcon fontSize="small" />,
  pptx: <SlideshowOutlinedIcon fontSize="small" />,
};

/** Split-style download picker for a document row.
 *
 * Single button when only one format is available (legacy rows),
 * dropdown Menu when both are. Uses ``doc.available_formats`` from
 * the API — primary format is the first entry. */
export function DocumentDownloadMenu({
  doc,
  ariaLabel,
}: {
  doc: DocumentSummary;
  /** Accessible name for the button, when several download buttons sit
   *  side by side (a document's earlier versions). */
  ariaLabel?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const formats: DocumentFormat[] = doc.available_formats?.length
    ? doc.available_formats
    : ["pdf"];
  const primary: DocumentFormat = formats[0] ?? "pdf";
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = (fmt: DocumentFormat): string => t(LABEL_KEYS[fmt]);

  if (formats.length < 2) {
    return (
      <Tooltip title={label(primary)}>
        <IconButton
          size="small"
          component="a"
          href={documentDownloadHref(doc.id, primary)}
          aria-label={ariaLabel ?? label(primary)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={t("common.download", "Download")}>
        <IconButton
          size="small"
          aria-label={
            ariaLabel ??
            t("documents_ui.download_document_aria", "Download document")
          }
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {formats.map((fmt) => (
          <MenuItem
            key={fmt}
            component="a"
            href={documentDownloadHref(doc.id, fmt)}
            onClick={() => setAnchor(null)}
            sx={{ gap: 1 }}
          >
            {ICONS[fmt]}
            {label(fmt)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
